import type { FeedPost } from '../../feed/types';

const MAX_DASHBOARD_POSTS = 5;

type FeedBadge = {
    icon: string;
    label: string;
    className: string;
};

export function getDashboardFeedPosts(feedPosts: FeedPost[]): FeedPost[] {
    return feedPosts
        .filter(post => !post.deleted)
        .toSorted((a, b) => {
            if (a.pinned && !b.pinned) return -1;
            if (!a.pinned && b.pinned) return 1;
            return b.createdAt - a.createdAt;
        })
        .slice(0, MAX_DASHBOARD_POSTS);
}

export function getFeedPreviewText(content: string): string {
    if (!content.trim()) return '';

    const previewContainer = document.createElement('div');
    previewContainer.innerHTML = content
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, ' ');

    return (previewContainer.textContent || '')
        .replace(/\s+/g, ' ')
        .trim();
}

export function formatFeedRelativeTime(timestamp: number, now = Date.now()): string {
    const diffMs = Math.max(0, now - timestamp);
    const diffMinutes = Math.floor(diffMs / 60_000);
    const diffHours = Math.floor(diffMs / 3_600_000);
    const diffDays = Math.floor(diffMs / 86_400_000);

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return new Date(timestamp).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
    });
}

export function getPostPreview(post: FeedPost): string {
    const text = getFeedPreviewText(post.content);
    if (text) return text;

    if (post.media.length > 0) return 'Media update';
    if (post.contentLinks.length > 0) return 'Linked content update';
    return 'Production update';
}

export function getPostBadge(post: FeedPost): FeedBadge {
    if (post.pinned) {
        return {
            icon: 'push_pin',
            label: 'Pinned',
            className: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
        };
    }

    if (post.postType === 'media' || post.media.length > 0) {
        return {
            icon: 'image',
            label: 'Media',
            className: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
        };
    }

    if (post.postType === 'announcement') {
        return {
            icon: 'campaign',
            label: 'Announcement',
            className: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
        };
    }

    if (post.mentions.length > 0) {
        return {
            icon: 'alternate_email',
            label: 'Mention',
            className: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
        };
    }

    return {
        icon: 'chat_bubble',
        label: 'Update',
        className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    };
}
